import React, {
  useState,
  useCallback,
  useEffect,
} from 'react';
import clsx from 'clsx';
import ArrowLeftIcon from '@mui/icons-material/ArrowBackIosNew';
import ArrowRightIcon from '@mui/icons-material/ArrowForwardIos';
import isNil from 'lodash/isNil';
import MediaPreview from 'pages/Dashboard/components/MediaPreview';
import Button from 'pages/Dashboard/components/Button';
import {
  FileLocalUrl,
} from 'pages/Dashboard/components/FilePicker';
import {
  RequestMultiMediaDelete,
} from 'pages/Dashboard/hooks/useDeleteMediaHandler';

type Props = {
  images: (string | null | undefined)[];
  onDelete?: (src: string) => void;
  onEdit?: (index: number, edited: FileLocalUrl[]) => void;
  className?: string;
};

function ImageCarousel({
  images,
  onDelete,
  onEdit,
  className,
}: Props) {
  const [activeIndex, setActiveIndex] = useState<number>(0);

  const validImages = images
    .map((img, idx) => ({ src: img, originalIndex: idx }))
    .filter((item) => !isNil(item.src)) as Array<{ src: string; originalIndex: number }>;

  useEffect(() => {
    if (activeIndex >= validImages.length && validImages.length > 0) {
      setActiveIndex(Math.max(0, validImages.length - 1));
    }
  }, [activeIndex, validImages.length]);

  const handlePrevious = useCallback(() => {
    setActiveIndex((idx) => Math.max(0, idx - 1));
  }, []);

  const handleNext = useCallback(() => {
    setActiveIndex((idx) => Math.min(validImages.length - 1, idx + 1));
  }, [validImages.length]);

  useEffect(() => {
    function handleKeyDown(event: KeyboardEvent) {
      if (event.key === 'ArrowLeft' && activeIndex > 0) {
        handlePrevious();
      } else if (event.key === 'ArrowRight' && activeIndex < validImages.length - 1) {
        handleNext();
      }
    }

    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [activeIndex, validImages.length, handlePrevious, handleNext]);

  const handleDelete = useCallback((meta: RequestMultiMediaDelete) => {
    if (onDelete && validImages[activeIndex]) {
      onDelete(validImages[activeIndex].src);
      if (activeIndex > 0) {
        setActiveIndex(activeIndex - 1);
      } else if (validImages.length > 1) {
        setActiveIndex(0);
      }
    }
  }, [activeIndex, onDelete, validImages]);

  const handleEdit = useCallback((edited: FileLocalUrl[]) => {
    if (onEdit && validImages[activeIndex]) {
      onEdit(validImages[activeIndex].originalIndex, edited);
    }
  }, [activeIndex, onEdit, validImages]);

  if (validImages.length === 0) {
    return null;
  }

  const currentImage = validImages[activeIndex];

  const disableLeft = activeIndex === 0;
  const disableRight = activeIndex === validImages.length - 1;

  return (
    <div className={clsx('flex flex-col gap-2 w-full', className)}>
      <div className="flex justify-center w-full bg-gray-900 rounded-lg max-h-[600px] relative">
        <div className="flex flex-col gap-1 w-full">
          <div className="overflow-hidden">
            <MediaPreview
              media={{
                url: { value: currentImage.src },
                fileName: `photo-${currentImage.originalIndex + 1}.png`,
              }}
              thumbnail
              onDelete={onDelete ? handleDelete : undefined}
              onEdit={onEdit ? handleEdit : undefined}
              className="w-full "
              coverImage={false}
              classes={{
                image: 'bg-gray-800',
              }}
            />
          </div>
        </div>

        {validImages.length > 1 && (
          <>
            <Button
              className={
                clsx(
                  '!absolute top-[45%] left-2 !min-w-0 !p-2 !rounded-full z-10',
                  {
                    ' !text-gray-200': disableLeft,
                    'hover:!bg-gray-700 !text-white': !disableLeft,
                  },
                )
              }
              variant="text"
              onClick={handlePrevious}
              disabled={disableLeft}
            >
              <ArrowLeftIcon className="!w-8 !h-8" />
            </Button>
            <Button
              className={
                clsx(
                  '!absolute top-[45%] right-2 !min-w-0 !p-2 !text-white !rounded-full z-10',
                  {
                    '!text-gray-200': disableRight,
                    'hover:!bg-gray-700 !text-white': !disableRight,
                  },
                )
              }
              variant="text"
              onClick={handleNext}
              disabled={disableRight}
            >
              <ArrowRightIcon className="!w-8 !h-8" />
            </Button>
          </>
        )}
      </div>

      {validImages.length > 1 && (
        <div className="flex flex-col items-center gap-2">
          <div className="flex justify-center gap-2">
            {validImages.map((item, idx) => (
              <button
                key={`indicator-${item.originalIndex}`}
                type="button"
                onClick={() => setActiveIndex(idx)}
                className={clsx(
                  'w-2 h-2 rounded-full transition-all',
                  {
                    'bg-blue-500 w-6': idx === activeIndex,
                    'bg-gray-300 hover:bg-gray-400': idx !== activeIndex,
                  },
                )}
                aria-label={`Go to image ${idx + 1}`}
              />
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

export default ImageCarousel;
